import React, { useState, useEffect, useRef } from 'react';
import { LogOut, ChevronDown, User, Calendar, Search, Sun, Moon, Bell, Headset } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useYear } from '../context/YearContext';
import useAuthStore from '../store/authStore';
import LanguageSwitcher from './LanguageSwitcher';
import Breadcrumb from './Breadcrumb';
import CenterSwitcher from './CenterSwitcher';
import api from '../services/api';
import { userDisplayName, userInitials } from '../utils/userDisplay';
import useOutsideClick from '../hooks/useOutsideClick';

export default function Header({ onNotificationClick, unreadCount = 0 }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n, t } = useTranslation();
  const { selectedYear, setSelectedYear, years } = useYear();
  const { user, logout } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [logo, setLogo] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved === 'dark';
  });
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';

  // Apply theme on mount and toggle
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Refs for outside click detection
  const yearSelectorRef = useRef(null);
  const userMenuRef = useRef(null);

  // Close dropdowns when clicking outside
  useOutsideClick(yearSelectorRef, () => setIsOpen(false), isOpen);
  useOutsideClick(userMenuRef, () => setIsUserMenuOpen(false), isUserMenuOpen);

  // Load logo on mount
  useEffect(() => {
    const loadLogo = async () => {
      try {
        // The route answers { logoUrl } bare, which api.get returns as is.
        const response = await api.get('/settings/logo');
        const url = response?.logoUrl || response?.data?.logoUrl;
        if (url) setLogo(url);
      } catch (error) {
        console.error('Error loading logo:', error);
      }
    };
    loadLogo();
  }, []);


  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const currentYearName = years.find(y => y.id === selectedYear)?.name || t('common.selectYear');

  return (
    <>
      {/* Enterprise Header */}
      <header
        style={{
          height: '64px',
          backgroundColor: 'var(--color-surface, #FFFFFF)',
          borderBottom: '1px solid var(--color-border, #E2E8F0)',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          position: 'sticky',
          top: 0,
          zIndex: 50
        }}
      >
        {/* Left Section: Logo + Breadcrumbs */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flex: 1, minWidth: 0 }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            {logo ? (
              <img
                src={logo}
                alt={t('settings.centerLogo')}
                style={{
                  height: '40px',
                  width: 'auto',
                  objectFit: 'contain',
                  maxWidth: '120px'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <img
                src="/logo.png"
                alt={t('header.appLogoAlt')}
                style={{
                  height: '40px',
                  width: 'auto',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
          </div>

          {/* Vertical Divider */}
          <div
            style={{
              height: '32px',
              width: '1px',
              backgroundColor: 'var(--color-border, #E2E8F0)',
              flexShrink: 0
            }}
          />

          {/* Breadcrumbs */}
          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <Breadcrumb isInHeader={true} />
          </div>
        </div>


        {/* Right Section: Global Actions & Profile */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}
        >
          {/* Center Switcher — only visible if the user belongs to 2+ centers */}
          <CenterSwitcher />

          {/* Year Selector - Compacted */}
          {years.length > 0 && (
            <div ref={yearSelectorRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setIsOpen(!isOpen)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '6px 12px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--color-text-secondary, #64748B)',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-hover, #F1F5F9)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <Calendar size={16} />
                <span style={{ display: window.innerWidth > 768 ? 'inline' : 'none' }}>
                  {currentYearName}
                </span>
                <ChevronDown
                  size={14}
                  style={{
                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }}
                />
              </button>

              {/* Year Dropdown */}
              {isOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    backgroundColor: 'var(--color-surface)',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    boxShadow: '0 10px 15px rgba(0,0,0,0.1)',
                    minWidth: '200px',
                    zIndex: 60,
                    overflow: 'hidden'
                  }}
                >
                  <div
                    style={{
                      padding: '4px 0',
                      maxHeight: '300px',
                      overflowY: 'auto'
                    }}
                  >
                    {years.map(year => (
                      <button
                        key={year.id}
                        onClick={() => {
                          setSelectedYear(year.id);
                          setIsOpen(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 16px',
                          border: 'none',
                          backgroundColor: selectedYear === year.id ? 'var(--color-hover, #F1F5F9)' : 'transparent',
                          color: selectedYear === year.id ? 'var(--color-primary-dark)' : 'var(--color-text-secondary, #334155)',
                          cursor: 'pointer',
                          textAlign: 'left',
                          fontSize: '14px',
                          fontWeight: selectedYear === year.id ? '600' : '400',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (selectedYear !== year.id) {
                            e.currentTarget.style.backgroundColor = 'var(--color-surface-alt)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedYear !== year.id) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        {year.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Notification Bell */}
          <button
            className="header-icon-btn"
            onClick={onNotificationClick}
            aria-label={t('header.notifications')}
            title={t('header.notifications')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '6px',
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              color: 'var(--color-text-secondary, #64748B)',
              transition: 'background-color 0.2s ease',
              position: 'relative',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-hover, #F1F5F9)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute',
                top: '2px',
                right: '2px',
                minWidth: '16px',
                height: '16px',
                borderRadius: '999px',
                backgroundColor: '#DC2626',
                color: '#FFFFFF',
                fontSize: '10px',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 4px',
                lineHeight: 1,
              }}>
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {/* Dark Mode Toggle */}
          <button
            className="header-icon-btn"
            onClick={() => setIsDarkMode(!isDarkMode)}
            aria-label={isDarkMode ? t('header.switchToLight') : t('header.switchToDark')}
            title={isDarkMode ? t('header.lightMode') : t('header.darkMode')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '6px',
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              color: 'var(--color-text-secondary, #64748B)',
              transition: 'background-color 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-hover, #F1F5F9)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          {/* Language Switcher */}
          <LanguageSwitcher />

          {/* Help Button */}
          <button
            className="header-icon-btn"
            onClick={() => navigate('/help')}
            title={t('common.help')}
            aria-label={t('common.help')}
            style={{
              width: '36px',
              height: '36px',
              minHeight: '36px',
              padding: 0,
              borderRadius: '8px',
              border: 'none',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'var(--color-text-secondary, #64748B)',
              transition: 'background-color 150ms ease, color 150ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-hover, #F1F5F9)';
              e.currentTarget.style.color = 'var(--color-text-primary, #1E293B)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--color-text-secondary, #64748B)';
            }}
          >
            <Headset size={20} strokeWidth={2} aria-hidden="true" focusable="false" />
          </button>

          {/* Divider */}
          <div
            style={{
              height: '24px',
              width: '1px',
              backgroundColor: 'var(--color-border, #E2E8F0)',
              margin: '0 8px'
            }}
          />

          {/* User Profile */}
          <div ref={userMenuRef} style={{ position: 'relative' }}>
            <button
              className="header-icon-btn"
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '8px',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-hover, #F1F5F9)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#FFFFFF',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                {userInitials(user)}
              </div>
              {window.innerWidth > 768 && (
                <span
                  style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: 'var(--color-text-primary, #1E293B)'
                  }}
                >
                  {userDisplayName(user, t('header.defaultUser'))}
                </span>
              )}
              <ChevronDown
                size={14}
                style={{
                  color: '#94A3B8',
                  transform: isUserMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}
              />
            </button>

            {/* User Dropdown Menu */}
            {isUserMenuOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 8px)',
                  right: isRTL ? 'auto' : 0,
                  left: isRTL ? 0 : 'auto',
                  backgroundColor: 'var(--color-surface)',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                  minWidth: '220px',
                  zIndex: 60,
                  overflow: 'hidden'
                }}
              >
                <div
                  style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #E2E8F0',
                    backgroundColor: 'var(--color-surface-alt)'
                  }}
                >
                  <div
                    style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--color-text-primary, #1E293B)',
                      marginBottom: '4px'
                    }}
                  >
                    {userDisplayName(user, t('header.defaultUser'))}
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      color: 'var(--color-text-tertiary, #64748B)'
                    }}
                  >
                    {user?.email || user?.role || t('header.defaultRole')}
                  </div>
                </div>
                <button
                  onClick={() => {
                    navigate('/account');
                    setIsUserMenuOpen(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: 'var(--color-text-secondary, #334155)',
                    cursor: 'pointer',
                    textAlign: isRTL ? 'right' : 'left',
                    fontSize: '14px',
                    transition: 'background-color 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontWeight: '500'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-surface-alt)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <User size={16} />
                  {t('header.myAccount')}
                </button>
                <div
                  style={{
                    borderTop: '1px solid #E2E8F0',
                    margin: '4px 0'
                  }}
                />
                <button
                  onClick={handleLogout}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: '#EF4444',
                    cursor: 'pointer',
                    textAlign: isRTL ? 'right' : 'left',
                    fontSize: '14px',
                    transition: 'background-color 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontWeight: '500'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <LogOut size={16} />
                  {t('header.logout')}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
}
